import { memo } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import LinkedBadge from "../common/LinkedBadge.jsx";

// Six-dot grip, same visual language as the dashboard's drag handle.
const gripIcon = (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <circle cx="9" cy="6" r="1.6" /><circle cx="15" cy="6" r="1.6" />
    <circle cx="9" cy="12" r="1.6" /><circle cx="15" cy="12" r="1.6" />
    <circle cx="9" cy="18" r="1.6" /><circle cx="15" cy="18" r="1.6" />
  </svg>
);

/**
 * One card on the Theory of Change board, with the two drags a card supports:
 * the connect-dot on its right edge (draw a chain link to the next column) and
 * the grip handle on its left edge (reorder within its own column). Both are
 * handle-scoped, so the card body itself stays a plain click target that opens
 * the element's detail. The reorder drag is dnd-kit's; the link drag is the
 * board's own pointer handling. Neither handler can fire the other — but the
 * board still has to suppress its hover highlight during a reorder, since
 * sliding cards move under a stationary cursor.
 *
 * Memoised, and every callback prop is stable by contract: the board re-renders
 * on every pointermove of a link drag (the live line follows the cursor), and
 * without memo that would re-run `useSortable` for every card on the board at
 * pointer rate. The per-card closures are built HERE, from stable props.
 *
 * @param {object} props
 * @param {{id: string, title: string, raw: object}} props.node - Navigator node rendered by this card
 * @param {number} props.level - Index of this node's step in STEP_ORDER (passed back to onPick)
 * @param {string} props.className - Card classes computed by the board (highlight/dim/drop states)
 * @param {(el: HTMLElement | null) => void} props.cardRef - Stable ref callback registering the card for edge measurement
 * @param {(id: string, level: number) => void} props.onPick - Open this element's detail
 * @param {(id: string | null) => void} props.onHover - Card hovered (id) or left (null); drives the chain highlight
 * @param {(id: string, entering: boolean) => void} props.onLinkDragOver - Pointer entered/left this card during a link drag
 * @param {(node: object) => void} props.onLinkDrop - Pointer released on this card (link drop)
 * @param {(id: string, e: React.PointerEvent) => void} props.onLinkDragStart - Start a link drag from this card's dot
 * @param {boolean} props.canSource - Whether this card offers an outgoing connect-dot
 * @param {boolean} props.canReorder - Whether the reorder handle is offered
 * @param {boolean} [props.linked] - Whether this element is paired with a parent portfolio (shows the linked badge)
 * @param {string} [props.portfolioName] - Name of that parent portfolio (badge tooltip)
 */
const TocChainNode = ({
  node,
  level,
  className,
  cardRef,
  onPick,
  onHover,
  onLinkDragOver,
  onLinkDrop,
  onLinkDragStart,
  canSource,
  canReorder,
  linked,
  portfolioName,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: node.id, disabled: !canReorder });

  return (
    <div
      className={`tocnav-chain-node-wrap${isDragging ? " is-sorting" : ""}`}
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      {canReorder && (
        <span
          className="tocnav-chain-grip"
          ref={setActivatorNodeRef}
          aria-label={`Versleep ${node.title || "dit element"} om de volgorde te wijzigen`}
          {...attributes}
          {...listeners}
        >
          {gripIcon}
        </span>
      )}
      <button
        type="button"
        className={className}
        ref={cardRef}
        onMouseEnter={() => onHover(node.id)}
        onMouseLeave={() => onHover(null)}
        onPointerEnter={() => onLinkDragOver(node.id, true)}
        onPointerLeave={() => onLinkDragOver(node.id, false)}
        onPointerUp={() => onLinkDrop(node)}
        onClick={() => onPick(node.id, level)}
      >
        <b>{node.title || "Naamloos"}</b>
        {linked && <LinkedBadge portfolioName={portfolioName} />}
      </button>
      {canSource && (
        <span
          className="tocnav-chain-dot"
          role="button"
          aria-label={`Sleep om ${node.title || "dit element"} te koppelen`}
          onPointerDown={(e) => onLinkDragStart(node.id, e)}
        />
      )}
    </div>
  );
};

export default memo(TocChainNode);
